import type { ReactNode } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import ArticleHero from '../../../components/news/ArticleHero';
import PullQuote from '../../../components/news/PullQuote';
import KeyPointsBox from '../../../components/news/KeyPointsBox';
import CommentBox from '../../../components/news/CommentBox';
import SourceCard from '../../../components/news/SourceCard';
import RelatedCards from '../../../components/news/RelatedCards';

/* ---- 記事内インライン部品（報道メディア型・帯は使わない） ---- */
function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}
function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        出典: {media}（<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>）
      </figcaption>
    </figure>
  );
}
function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}
const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function JumeirahBurjAlArab25thAnniversaryPage() {
  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'ニュース・お知らせ', href: '/news' },
    { label: 'Jumeirah Burj Al Arab 25周年記念' }
  ];

  const article = {
    category: 'ラグジュアリーホスピタリティ・記念行事',
    date: '2024年11月29日',
    title: 'Jumeirah Burj Al Arab 25周年記念',
    subtitle: '世界最高級ホテルにおける25年間のバトラーサービスの継続',
    heroImage: '/images/generated/jba-fs-096-news_jumeirah-burj-al-arab-25th-annivers.jpg',
    lead: 'Dubai HoldingのメンバーであるJumeirahが、2024年12月1日にJumeirah Burj Al Arabの25周年を祝いました。1999年のオープン以来、このホテルはUAEにおける高級ホスピタリティを再定義し、すべてのスイートでバトラーサービスを提供する戦略的意義を示し続けています。25年間にわたる革新的なイベントとバトラーサービスの卓越性が、世界的なラグジュアリーホテルの基準を確立しています。',
    sections: [
      {
        heading: '25周年記念とブランドの進化',
        image: '/images/generated/jba-fs-097-news_jumeirah-burj-al-arab-25th-annivers-v3.jpg', aspect: 'aspect-[3/4]',
        alt: 'ダウ船の帆の形をした象徴的なJumeirah Burj Al Arabの建築のイメージ',
        paragraphs: [
          '1999年のオープン以来、Jumeirah Burj Al Arabは象徴的なダウ船の帆の形をしたランドマークとして、観光における卓越性の世界的に認知されたシンボルとなっています。25周年は、Jumeirahブランドにとって重要な節目であり、2024年初めに新しいビジュアルアイデンティティを発表した後、持続可能な成長に焦点を当てています。',
          'Jumeirahは地域的および国際的にゲートウェイシティとリゾート目的地での展開を続けながら、高級ホスピタリティ分野における業界を変えるリーダーとしての地位を再確認しています。この成長戦略に沿って、Jumeirah Red Sea（サウジアラビア）、Jumeirah Marsa Al Arab（アラブ首長国連邦）、Jumeirah Le Richemond Geneva（スイス）などの新しい物件を発表しています。'
        ]
      },
      {
        heading: 'アラビア文化のホスピタリティと執事サービスの融合',
        image: '/images/generated/jba-fs-098-news_jumeirah-burj-al-arab-25th-annivers-v3.jpg', aspect: 'aspect-[3/4]',
        alt: '温かさと寛大さに触発されたアラビア文化のホスピタリティのイメージ',
        paragraphs: [
          'COO兼暫定CEOのThomas Meier氏は、「象徴的なJumeirah Burj Al Arabをオープンしてから25年を祝うにあたり、私たちはアラビア文化の温かさと寛大さに触発された高級ホスピタリティにおける卓越性の遺産を振り返ります」とコメントしています。',
          '旅行者を歓迎し、文化と会話を共有するこの伝統は、Jumeirahのビジョンの中心にあります。単に美しい目的地を創造するだけでなく、世界をつなぐ空間を創造することで、物件はグローバルなマジュリス（集会所）であり続け、目的のある独自の文化的つながりの機会を提供しています。'
        ]
      },
      {
        heading: '25年間の記念すべきイベント',
        image: '/images/generated/jba-fs-099-news_jumeirah-burj-al-arab-25th-annivers-v3.jpg', aspect: 'aspect-[3/4]',
        alt: '重力に逆らう眩しいイベントで世界を魅了してきたラグジュアリーホテルのイメージ',
        paragraphs: [
          'Jumeirah Burj Al Arabは25年間、期待、想像、そして重力そのものに逆らう眩しいイベントと畏敬の念を抱かせるスタントで世界を魅了してきました。ヘリパッドでの大胆な挑戦として、2013年にはSkydive DubaiのNasser Al Neyadi閣下が初めてヘリパッドにスカイダイビング着陸を成功させました。',
          '2005年にはAndre AgassiとRoger Federerが雲の上でテニスマッチを行い、2017年にはAnthony JoshuaとSeb Eubankの天空での対決が実現しました。2021年には、David GuettaがUNICEFとDubai Caresを支援する「United at Home」シリーズの一環としてDJセットを実施し、パンデミックの影響を受けた学生と教師のために資金を調達しました。'
        ]
      },
      {
        heading: 'バトラーサービスの卓越性とブランドアイデンティティ',
        image: '/images/generated/jba-fs-100-news_jumeirah-burj-al-arab-25th-annivers-v3.jpg', aspect: 'aspect-video',
        alt: 'すべてのスイートに提供されるパーソナライズされたバトラーサービスの卓越性のイメージ',
        paragraphs: [
          'Jumeirah Burj Al Arabは、パーソナライズされたラグジュアリーの前例のない基準を設定し続けています。すべてのスイートにバトラーサービスを提供し、ビスポークRolls Royce車両のフリートを完備しています。この戦略的意義は、高級ホスピタリティにおける執事サービスの不可欠性を明確に示しています。',
          'Skyview Barでは、かつて世界で最も高価なカクテル「27.321」（バーが27階、321メートルの高さに位置することから着想）を27,321ディルハムで提供し、ブランドアイデンティティの中核としての執事サービスの価値を体現しています。'
        ]
      },
      {
        heading: '社会貢献と持続可能性への取り組み',
        image: '/images/generated/jba-fs-101-news_jumeirah-burj-al-arab-25th-annivers-v4.jpg', aspect: 'aspect-[3/4]',
        alt: '絶滅危惧種のカメを救助・リハビリする海洋保全プロジェクトのイメージ',
        paragraphs: [
          '目を引く豪華さを超えて、Jumeirah Burj Al ArabはJumeirahの継続的な良き隣人であり、拠点とする場所を保護する取り組みの中心です。Dubai Turtle Rehabilitation Project（DTRP）がこの象徴的なホテルに設置されており、Jumeirahの継続的な海洋保全活動の一環として機能しています。',
          'このプロジェクトを通じて、何千もの絶滅危惧種のカメが救助、リハビリテーション、野生に戻されており、ラグジュアリーホテルとしての社会的責任を果たしながら、バトラーサービスの価値観である「奉仕の精神」を環境保護にも拡張しています。'
        ]
      }
    ],
    comment: 'Jumeirah Burj Al Arabの25周年記念は、世界最高級ホテルにおける25年間のバトラーサービスの継続が示す価値を明確に証明する重要な節目として、弊協会としても深く敬意を表しております。すべてのスイートでバトラーサービスを提供する戦略的意義と、高級ホスピタリティにおける執事サービスの不可欠性は、ブランドアイデンティティの中核としての執事サービスの価値を世界に示しています。アラビア文化のホスピタリティと執事サービスの融合という独自のアプローチは、文化的背景を活かしたサービス開発の可能性を示唆しており、日本の高級ホテル業界への示唆としても非常に価値があります。このような国際的な成功事例を参考に、私どもとしても、日本独自の「おもてなし」文化と執事サービスの融合による新たな価値創造に向けて、積極的に取り組んでまいります。',
    source: [
      { label: '出典元', value: 'Jumeirah公式プレスリリース', link: false },
      { label: '記事URL', value: 'https://www.jumeirah.com/en/jumeirah-group/press-centre/press-releases/press-releases-for-2024/25-years-of-redefining-luxury-celebrating-the-legacy-of-jumeirah-burj-al-arab', link: true },
      { label: 'アクセス日', value: '2025年10月', link: false }
    ],
    related: [
      { to: '/news/raffles-sentosa-butler-package', image: '/images/generated/jba-fs-102-news_jumeirah-burj-al-arab-25th-annivers-v4.jpg', aspect: 'aspect-[3/4]', alt: 'Raffles Sentosa Singapore「The Butler Did It」パッケージの関連記事', title: 'Raffles Sentosa Singapore「The Butler Did It」パッケージ', desc: '1泊約55万円の究極のビスポークラグジュアリー体験' },
      { to: '/news/raffles-butler-campaign', image: '/images/generated/jba-fs-103-news_jumeirah-burj-al-arab-25th-annivers-v4.jpg', aspect: 'aspect-[3/4]', alt: 'Raffles「The Butler Did It」グローバルキャンペーンの関連記事', title: 'Raffles「The Butler Did It」グローバルキャンペーン', desc: 'ラグジュアリーホテルの執事サービス革新' }
    ]
  };
  const S = article.sections;

  const sources = [
    { label: '出典元', value: 'Jumeirah公式プレスリリース', link: false },
    { label: '基本データ', value: 'https://en.wikipedia.org/wiki/Burj_Al_Arab', link: true },
    { label: '25周年（公式）', value: 'https://mediahub.jumeirah.com/jumeirah-burj-al-arab-introduces-a-limited-edition-chocolate-to-mark-its-25th-anniversary', link: true },
    { label: '25周年報道', value: 'https://globalloyalty.org/article/16669/25-years-of-redefining-luxury-celebrating-the-legacy-of-jumeirah-burj-al-arab', link: true },
    { label: 'アクセス日', value: '2026年7月', link: false }
  ];
  const toc = [
    { id: 's-anniversary', label: '25周年記念とブランドの進化' },
    { id: 's-facts', label: 'バージュ・アル・アラブの基本データ' },
    { id: 's-culture', label: 'アラビア文化のホスピタリティと執事サービスの融合' },
    { id: 's-events', label: '25年間の記念すべきイベント' },
    { id: 's-butler', label: 'バトラーサービスの卓越性とブランドアイデンティティ' },
    { id: 's-legacy', label: '25周年記念の施策とレガシー' },
    { id: 's-social', label: '社会貢献と持続可能性への取り組み' }
  ];

  return (
    <Layout>
      <ArticleHero eyebrow="News / Luxury" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="この記事のポイント"
              points={[
                'Jumeirahが2024年12月1日にJumeirah Burj Al Arabの開業25周年を祝った',
                '1999年開業・高さ321mの全室スイート（202室）で、すべてのスイートにバトラーサービスを提供している',
                '25年間の革新的イベントとバトラーサービスの卓越性が、世界のラグジュアリーホテルの基準を確立した'
              ]}
            />

            <SectionHeading id="s-anniversary" en="Anniversary">{S[0].heading}</SectionHeading>
            <div className="space-y-6">{S[0].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-facts" en="Landmark Facts">バージュ・アル・アラブの基本データ</SectionHeading>
            <div className="space-y-6">
              <p className={P}>バージュ・アル・アラブは<Stat>1999年12月1日</Stat>に開業した、ダウ船の帆をかたどる高さ<Stat>321メートル</Stat>（頂上階は197.5メートル）のランドマークです。全室スイート仕様で、客室数は<Stat>202室</Stat>とされています。</p>
              <p className={P}>星評価はForbes Travel Guideおよびドバイ観光局で最高位の5つ星。しばしば「7つ星」と紹介されますが、これはジャーナリスト由来の俗称であり、運営元のJumeirahが公式に称したものではありません。</p>
            </div>
            <SourceShot
              src="/images/news-sources/jumeirah-wikipedia.jpg"
              alt="バージュ・アル・アラブの開業年・高さ・客室数などの基本情報を掲載するWikipedia英語版のページのスクリーンショット"
              media="Wikipedia（Burj Al Arab）"
              url="https://en.wikipedia.org/wiki/Burj_Al_Arab"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="アラビア文化の温かさと寛大さに触発された、高級ホスピタリティにおける卓越性の遺産を振り返ります。" cite="Thomas Meier / Jumeirah COO兼暫定CEO" />
            </div>

            <SectionHeading id="s-culture" en="Culture">{S[1].heading}</SectionHeading>
            <div className="space-y-6">{S[1].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-events" en="Iconic Events">{S[2].heading}</SectionHeading>
            <div className="space-y-6">{S[2].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-butler" en="Butler Service">{S[3].heading}</SectionHeading>
            <div className="space-y-6">{S[3].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-legacy" en="25th Anniversary">25周年記念の施策とレガシー</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Jumeirahは開業25周年にあたる<Stat>2024年12月1日</Stat>、限定版のチョコレートバー（AED 175）を発売し、「25の象徴的な瞬間」を振り返る特集を展開しました。</p>
              <p className={P}>記念発表は「次の25年へ向けた洗練と保存」を掲げ、2026年4月以降に予定される約18か月間の大規模改修プログラムへとつながる文脈で語られています。四半世紀にわたり全室バトラーサービスを守り続けたレガシーが、次の時代へと引き継がれます。</p>
            </div>
            <SourceShot
              src="/images/news-sources/jumeirah-mediahub.jpg"
              alt="25周年を記念した限定チョコレートの発売を伝えるJumeirah公式メディアハブのページのスクリーンショット"
              media="Jumeirah Media Hub（公式）"
              url="https://mediahub.jumeirah.com/jumeirah-burj-al-arab-introduces-a-limited-edition-chocolate-to-mark-its-25th-anniversary"
            />

            <SectionHeading id="s-social" en="Sustainability">{S[4].heading}</SectionHeading>
            <div className="space-y-6">{S[4].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="目次" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">開業</dt><dd className="text-[#333] mt-0.5">1999年12月1日</dd></div>
                  <div><dt className="text-[#8f7a56]">建築高</dt><dd className="text-[#333] mt-0.5">321m</dd></div>
                  <div><dt className="text-[#8f7a56]">客室</dt><dd className="text-[#333] mt-0.5">全202スイート</dd></div>
                  <div><dt className="text-[#8f7a56]">星評価</dt><dd className="text-[#333] mt-0.5">5つ星（Forbes）</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="全日本執事協会からのコメント" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="出典" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="関連記事" items={article.related} readLabel="詳しく" backHref="/news" backLabel="ニュース・お知らせ一覧へ戻る" />
    </Layout>
  );
}
